var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "../../lib/utils.js";
// modal=true (default): overlay blocks outside interaction + focus trap.
// Pass modal={false} only for rare non-blocking sheets (visual dim only).
var SheetContext = React.createContext({ modal: true });
var Sheet = function (_a) {
    var _b = _a.modal, modal = _b === void 0 ? true : _b, children = _a.children, props = __rest(_a, ["modal", "children"]);
    return (_jsx(SheetContext.Provider, { value: { modal: modal }, children: _jsx(SheetPrimitive.Root, __assign({ modal: modal }, props, { children: children })) }));
};
Sheet.displayName = "Sheet";
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = SheetPrimitive.Portal;
var SheetOverlay = React.forwardRef(function (_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx(SheetPrimitive.Overlay, __assign({ className: cn("fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", className) }, props, { ref: ref })));
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetVariants = cva("fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500", {
    variants: {
        side: {
            top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
            bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            left: "inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
            right: "inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        },
        size: {
            sm: "w-3/4 sm:max-w-sm",
            md: "w-4/5 sm:max-w-md",
            lg: "w-5/6 sm:max-w-lg",
            xl: "w-full sm:max-w-xl",
            "2xl": "w-full sm:max-w-2xl",
            full: "w-full",
        },
    },
    defaultVariants: { side: "right", size: "sm" },
});
// Common Radix layers to ignore
var RADIX_LAYER_SELECTORS = [
    "[data-radix-dialog-content]",
    "[data-radix-dialog-overlay]",
    "[data-radix-popper-content-wrapper]",
    "[data-radix-popover-content]",
    "[data-radix-dropdown-menu-content]",
    "[data-radix-select-content]",
    "[data-radix-hover-card-content]",
    "[data-radix-tooltip-content]",
    "[data-radix-context-menu-content]",
    '[role="menu"]',
    '[role="listbox"]',
];
// MUI portal layers to ignore (Menu, Popover, Dialog, etc.)
var MUI_LAYER_SELECTORS = [
    ".MuiPopover-root",
    ".MuiModal-root",
    ".MuiMenu-root",
    ".MuiDialog-root",
];
// Your chatbot + lift-ai dialog layers to ignore
var LIFT_AI_SELECTORS = [
    "[data-lift-ai-root]",
    ".chatbot-v2",
    ".html-view-iframe",
    "[data-lift-ai-dialog-overlay]",
    "[data-lift-ai-dialog-content]",
    // match ALL of your dialog pieces: dialog, dialog-content, dialog-overlay, dialog-portal, etc.
    '[data-slot^="dialog"]',
    // generic dialog hints for robustness across MFEs
    '[role="dialog"]',
    '[aria-modal="true"]',
];
// Toast notification selectors to ignore
var TOAST_SELECTORS = [
    ".Toastify",
    ".Toastify__toast-container",
    ".Toastify__toast",
    ".toast-success-container",
    ".toast-warning-container",
    ".toast-failure-container",
    ".toast-info-container",
    ".toast-style-ltp",
];
// Media popup selectors to ignore
var MEDIA_POPUP_SELECTORS = [
    '[data-media-popup="true"]',
    '[data-media-popup-content="true"]',
];
// Default places we ignore when deciding to dismiss
var DEFAULT_IGNORE_SELECTORS = __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], LIFT_AI_SELECTORS, true), RADIX_LAYER_SELECTORS, true), MUI_LAYER_SELECTORS, true), TOAST_SELECTORS, true), MEDIA_POPUP_SELECTORS, true);
var SheetContent = React.forwardRef(function (_a, ref) {
    var _b = _a.side, side = _b === void 0 ? "right" : _b, _c = _a.size, size = _c === void 0 ? "sm" : _c, className = _a.className, children = _a.children, ignoreInteractOutsideSelectors = _a.ignoreInteractOutsideSelectors, _d = _a.includeDefaultIgnoreSelectors, includeDefaultIgnoreSelectors = _d === void 0 ? true : _d, userOnPointerDownOutside = _a.onPointerDownOutside, userOnInteractOutside = _a.onInteractOutside, userOnFocusOutside = _a.onFocusOutside, userOnEscapeKeyDown = _a.onEscapeKeyDown, restProps = __rest(_a, ["side", "size", "className", "children", "ignoreInteractOutsideSelectors", "includeDefaultIgnoreSelectors", "onPointerDownOutside", "onInteractOutside", "onFocusOutside", "onEscapeKeyDown"]);
    var combinedIgnoreSelectors = React.useMemo(function () {
        var base = includeDefaultIgnoreSelectors ? DEFAULT_IGNORE_SELECTORS : [];
        return __spreadArray(__spreadArray([], base, true), (ignoreInteractOutsideSelectors !== null && ignoreInteractOutsideSelectors !== void 0 ? ignoreInteractOutsideSelectors : []), true);
    }, [ignoreInteractOutsideSelectors, includeDefaultIgnoreSelectors]);
    var shouldIgnore = React.useCallback(function (target) {
        if (!(target instanceof HTMLElement))
            return false;
        if (combinedIgnoreSelectors.some(function (sel) { return target.closest(sel); }))
            return true;
        // When any Radix popper layer (dropdown, popover, select, etc.) is open,
        // block Sheet dismissal so the popper closes first instead of both closing
        if (document.querySelector('[data-radix-popper-content-wrapper]'))
            return true;
        // Same for MUI portal layers (Menu, Popover, Dialog)
        if (document.querySelector('.MuiModal-root'))
            return true;
        return false;
    }, [combinedIgnoreSelectors]);
    var handlePointerDownOutside = function (e) {
        if (shouldIgnore(e.target))
            e.preventDefault();
        userOnPointerDownOutside === null || userOnPointerDownOutside === void 0 ? void 0 : userOnPointerDownOutside(e);
    };
    var handleInteractOutside = function (e) {
        if (shouldIgnore(e.target))
            e.preventDefault();
        userOnInteractOutside === null || userOnInteractOutside === void 0 ? void 0 : userOnInteractOutside(e);
    };
    // Prevent close when focus moves into any dialog/content we ignore
    var handleFocusOutside = function (e) {
        if (shouldIgnore(e.target))
            e.preventDefault();
        userOnFocusOutside === null || userOnFocusOutside === void 0 ? void 0 : userOnFocusOutside(e);
    };
    // Pressing Esc inside an inner dialog should not bubble to close the Sheet
    var handleEscapeKeyDown = function (e) {
        if (shouldIgnore(e.target))
            e.preventDefault();
        userOnEscapeKeyDown === null || userOnEscapeKeyDown === void 0 ? void 0 : userOnEscapeKeyDown(e);
    };
    var isModal = React.useContext(SheetContext).modal;
    return (_jsxs(SheetPortal, { children: [isModal ? (_jsx(SheetOverlay, {})) : (
            // purely visual backdrop; don't steal clicks
            _jsx("div", { "aria-hidden": true, className: "fixed inset-0 z-40 bg-black/60 pointer-events-none\n                     data-[state=open]:animate-in data-[state=closed]:animate-out\n                     data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" })), _jsxs(SheetPrimitive.Content, __assign({ ref: ref, 
                // Light: --background (white). Dark: --background (dark page surface).
                // ! so plain MFE `bg-white` cannot wash dark sheets. Consumer `className` last for intentional overrides.
                className: cn(sheetVariants({ side: side, size: size }), '!bg-background', className), onPointerDownOutside: handlePointerDownOutside, onInteractOutside: handleInteractOutside, onFocusOutside: handleFocusOutside, onEscapeKeyDown: handleEscapeKeyDown }, restProps, { children: [children, _jsxs(SheetPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary", children: [_jsx(X, { className: "h-4 w-4" }), _jsx("span", { className: "sr-only", children: "Close" })] })] }))] }));
});
SheetContent.displayName = SheetPrimitive.Content.displayName;
var SheetHeader = function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ className: cn("flex flex-col space-y-2 text-center sm:text-left", className) }, props)));
};
SheetHeader.displayName = "SheetHeader";
var SheetFooter = function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pt-4", className) }, props)));
};
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React.forwardRef(function (_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx(SheetPrimitive.Title, __assign({ ref: ref, className: cn("text-lg font-semibold text-foreground", className) }, props)));
});
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React.forwardRef(function (_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx(SheetPrimitive.Description, __assign({ ref: ref, className: cn("text-sm text-muted-foreground pb-4", className) }, props)));
});
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export { Sheet, SheetPortal, SheetOverlay, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription, };
